import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { v4 as uuidv4 } from 'uuid'
import PropTypes from 'prop-types'
import {
  Stack,
  Grid,
  Box,
  Divider,
  Typography,
  Button,
  FormHelperText,
  IconButton,
  Collapse,
  Tooltip,
  InputLabel,
} from '@mui/material'
import { ExposureOutlined as ExposureIcon, ExposureTwoTone as ExposureTwoToneIcon } from '@mui/icons-material'
import dayjs from 'dayjs'
import {
  useCreateFinishedGoodMutation,
  useGetFinishedGoodByIdQuery,
  useUpdateFinishedGoodMutation,
  useVoidFinishedGoodMutation,
} from 'services/finishedGoods'
import { showFormModal, setSnackBar } from 'state/ui'

import BaleTag from 'components/generic/printable/BaleTag'
import PrintDialog from 'components/generic/printable/PrintDialog'
import Input from 'components/generic/form/Input'
import Select from 'components/generic/form/Select'
import MaterialAutocomplete from 'components/generic/form/AsyncAutocomplete/MaterialAutocomplete'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import FormModalActionBar from 'components/generic/modals/form-modal/FormModalActionBar'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import Loading from 'components/generic/Loading'
import CameraButton from 'components/generic/buttons/CameraButton'
import ScalePopover from 'components/generic/popovers/ScalePopover'
import MultiImageBlade from 'components/generic/modals/blade-modal/MultiImageBlade'
import DateTimePicker from 'components/generic/form/DateTimePicker'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import StyledLink from 'components/generic/typography/StyledLink.jsx'

import { formOptions, formOptionsGenerator } from 'utils/validation/finishedGood'
import { PRINT_DIALOG_TYPES, CHURN_ZERO_EVENTS as cz, MODAL_VIEW_MODES, UUIDRegex } from 'utils/constants'
import { trackEvent } from 'utils/tracking'
import FORM_MODALS from 'utils/ui/modals'
import { useScaleState } from 'utils/custom-hooks/useScaleState'
import { generateRequestImagesFunction } from 'utils/images'
import { checkIfDateIsLocked } from 'utils/helpers'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'

const formatData = (data, listOfFinishedGoods, defaultYardUm) => {
  return {
    inboundTicketMaterial: data?.materialRowId
      ? {
          id: data.materialRowId,
        }
      : undefined,
    materialTypeId: data.materialTypeId,
    orderEntryId: data.orderEntryId ?? null,
    effectiveDate: new Date(data.effectiveDate).toISOString(),
    weight: {
      units: defaultYardUm,
      gross: Number(data.gross),
      tare: Number(data.tare),
      net: Number(data.net),
    },
    type: listOfFinishedGoods.find(item => data.type === item.id)?.name,
    notes: [
      { name: data?.externalNotes, internal: false },
      { name: data?.internalNotes, internal: true },
    ],
    purchaseOrderNumber: data.purchaseOrderNumber,
    grossImages: data.grossImages,
    tareImages: data.tareImages,
    inboundTicketId: data.inboundTicketId,
    itemCount: data.itemCount || data.itemCount === 0 ? parseInt(data.itemCount) : undefined,
  }
}

const CreateFinishedGoodContainer = ({ onFormStateChange, onHideModal }) => {
  const { t } = useTranslation('inboundTicket')
  const { defaultYardTimezone } = useSelector(state => state.auth)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { viewMode, finishedGoodProps, navigateTo, handleFinishedGoodCreated, handleFinishedGoodDeleted } = useSelector(
    state => state.ui.formModalProps,
  )

  let options = formOptions // default options
  if (finishedGoodProps && finishedGoodProps?.rowData) {
    options = formOptionsGenerator(finishedGoodProps?.rowData?.gross, finishedGoodProps?.rowData?.tare)
  }
  const { handleSubmit, control, formState, reset, setValue, getValues, watch } = useForm(options)
  const { errors } = formState

  const { pathname } = useLocation()
  const cleanedPathname = pathname[pathname.length - 1] === '/' ? pathname.slice(0, -1) : pathname
  const [finishedGoodId, setFinishedGoodId] = useState('')
  const [title, setTitle] = useState('Create New Finished Good')
  const [showVoidModal, setShowVoidModal] = useState(false)
  const [showPrintBaleTagModal, setShowPrintBaleTagModal] = useState(false)
  const [scalePopoverData, setScalePopoverData] = useState({})
  const [multiImageBladeData, setMultiImageBladeData] = useState(null)
  const [linkedInboundTicketInfo, setLinkedInboundTicketInfo] = useState(null)
  const { isFetchingScaleReading, showScaleButton, readScale } = useScaleState()
  const { requestImages } = generateRequestImagesFunction()
  const listOfFinishedGoods = useRef([])
  const grossRef = useRef(null)
  const tareRef = useRef(null)
  const [showGrossAdjustment, setShowGrossAdjustment] = useState(false)
  const [showTareAdjustment, setShowTareAdjustment] = useState(false)

  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}
  const { selectedScaleIds, selectedCameraIds, scaleCameras } = useSelector(state => state.devices)
  const isNativeCamera =
    selectedCameraIds?.length === 0 && Object.values(scaleCameras || {}).every(cameras => !cameras?.length)
  const { enablePullApartFeatures, enableLockAccountingV2 } = useSelector(state => state.featureFlags?.variables || {})

  const values = getValues()
  const grossImages = watch('grossImages')
  const tareImages = watch('tareImages')

  const { data: finishedGoodItem, isLoading: isGettingData } = useGetFinishedGoodByIdQuery(
    {
      id: finishedGoodId,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !finishedGoodId || !UUIDRegex.test(finishedGoodId),
    },
  )

  const [createFinishedGood, { isLoading: isCreating }] = useCreateFinishedGoodMutation()
  const [updateFinishedGood, { isLoading: isUpdating }] = useUpdateFinishedGoodMutation()
  const [voidFinishedGood, { isLoading: isDeletingFinishedGood }] = useVoidFinishedGoodMutation()

  const isLoading = isGettingData || isCreating || isUpdating || isDeletingFinishedGood

  const isInLockedAccountingPeriodV1 = useMemo(() => {
    if (enableLockAccountingV2) return false // if lock accounting V2 is turned on, we do not need to disable any fields
    if (!finishedGoodItem || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return (
      new Date(finishedGoodItem.effectiveDate).getTime() < new Date(lockAccountingPeriod.lockAccountingDate).getTime()
    )
  }, [lockAccountingPeriod, finishedGoodItem])

  const isInLockedAccountingPeriodV2 = useMemo(() => {
    if (!enableLockAccountingV2) return false
    if (!finishedGoodItem || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return checkIfDateIsLocked(lockAccountingPeriod, finishedGoodItem.effectiveDate)
  }, [lockAccountingPeriod, finishedGoodItem])

  useEffect(() => {
    const inboundTicket = finishedGoodProps?.inboundTicketData || finishedGoodItem?.inboundTicket
    if (inboundTicket) {
      setLinkedInboundTicketInfo({
        id: inboundTicket?.id,
        ticketId: inboundTicket?.ticketId,
        customerId: inboundTicket?.customer?.id,
        customerName: inboundTicket?.customer?.displayName,
      })

      const rowData = finishedGoodProps?.rowData
      if (rowData?.finishedGood?.id) {
        setFinishedGoodId(rowData.finishedGood.id)
      }
    }
  }, [finishedGoodProps, finishedGoodItem])

  useEffect(() => {
    const rowData = finishedGoodProps?.rowData
    if (rowData?.finishedGood?.id) {
      // do nothing, as finishedGoodId is already set
      return
    }
    setFinishedGoodId(viewMode === MODAL_VIEW_MODES.NEW ? 'new' : cleanedPathname.split('/').pop())
  }, [])

  useEffect(() => {
    const newFinishedGoodsList = (settings?.inventorySettings?.finishedGoods?.listOfFinishedGoods || [])
      .filter(item => !!item.active)
      .map(item => ({
        ...item,
        name: item.display,
      }))

    if (finishedGoodItem) {
      const typeIsInNewList = newFinishedGoodsList.find(item => finishedGoodItem?.type === item?.name)?.id || ''
      if (!typeIsInNewList) {
        newFinishedGoodsList.push({
          id: uuidv4(),
          name: finishedGoodItem?.type,
        })
      }
    }
    listOfFinishedGoods.current = newFinishedGoodsList
  }, [settings, finishedGoodItem])

  useEffect(() => {
    if (finishedGoodProps) {
      reset({
        materialTypeId: finishedGoodProps?.materialTypeId,
        materialName: finishedGoodProps?.materialName,
        effectiveDate: dayjs(finishedGoodProps?.effectiveDate),
        gross: finishedGoodProps?.gross || 0,
        tare: finishedGoodProps?.tare || 0,
        net: finishedGoodProps?.net || 0,
        grossImages: [],
        tareImages: [],
        inboundTicketId: finishedGoodProps?.inboundTicketData?.id,
        itemCount: finishedGoodProps?.itemCount,
        materialRowId: finishedGoodProps?.rowData?.id,
      })
    }
  }, [finishedGoodProps])

  const handleOpenPrintDialog = () => setShowPrintBaleTagModal(true)

  const handleClosePrintDialog = () => setShowPrintBaleTagModal(false)

  const onModalClose = useCallback(() => {
    onHideModal()
    if (viewMode !== MODAL_VIEW_MODES.NEW && navigateTo) {
      navigate(navigateTo)
    }
  })

  const handleError = error => {
    trackEvent({
      eventName: viewMode === MODAL_VIEW_MODES.EDIT ? cz.UPDATE_FINISHED_GOOD : cz.CREATE_FINISHED_GOOD,
      errorMsg: error,
    })
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage:
          error?.data?.error ||
          `There was an error ${viewMode === MODAL_VIEW_MODES.EDIT ? 'updating' : 'creating'} the finished good`,
      }),
    )
  }

  const handleVoidError = error => {
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: error?.data?.error || 'There was an error voiding the finished good',
      }),
    )
  }

  const handleVoidSuccess = () => {
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: 'Finished good successfully voided',
      }),
    )
    if (handleFinishedGoodDeleted) {
      handleFinishedGoodDeleted({ id: finishedGoodId })
    }
  }

  const onSubmit = async data => {
    const finishedGood = formatData(data, listOfFinishedGoods.current, defaultYardUm)
    if (viewMode === MODAL_VIEW_MODES.EDIT) {
      await updateFinishedGood({
        id: finishedGoodId,
        body: { finishedGood },
      })
        .unwrap()
        .then(payload => {
          reset({
            ...data,
            effectiveDate: dayjs(data.effectiveDate),
          })
          trackEvent(cz.UPDATE_FINISHED_GOOD)
          if (handleFinishedGoodCreated !== undefined) handleFinishedGoodCreated({ ...finishedGood, id: payload.id })
          dispatch(
            showFormModal({
              formModalName: FORM_MODALS.FINISHED_GOOD_CREATED,
              formModalProps: {
                finishedGood: payload,
                navigateTo,
                viewMode: MODAL_VIEW_MODES.EDIT,
              },
            }),
          )
        })
        .catch(error => handleError(error))
    } else {
      await createFinishedGood({ body: { finishedGood } })
        .unwrap()
        .then(payload => {
          if (handleFinishedGoodCreated !== undefined) handleFinishedGoodCreated({ ...finishedGood, id: payload.id })
          reset({
            ...data,
            effectiveDate: dayjs(data.effectiveDate),
          })
          trackEvent(cz.CREATE_FINISHED_GOOD)
          dispatch(
            showFormModal({
              formModalName: FORM_MODALS.FINISHED_GOOD_CREATED,
              formModalProps: {
                finishedGood: payload,
                navigateTo,
              },
            }),
          )
        })
        .catch(error => handleError(error))
    }
  }

  const callReadScale = ({ weightType, scaleId = '' }) => {
    readScale(scaleData => {
      if (weightType === 'gross') {
        onChangeGross(scaleData.weight)
        grossRef?.current?.focus
      } else if (weightType === 'tare') {
        onChangeTare(scaleData.weight)
        tareRef?.current?.focus
      }
    }, scaleId)
  }

  const scaleButtonClickHandler = (weightType, event) => {
    if (!['gross', 'tare'].includes(weightType)) throw new Error('Invalid weight type!')

    if (selectedScaleIds?.length > 1) {
      setScalePopoverData({
        anchorEl: event.target,
        weightType,
      })
    } else {
      callReadScale({ weightType })
      if (weightType === 'gross') {
        callRequestImages(grossImages, 'grossImages', selectedScaleIds[0])
      } else {
        callRequestImages(tareImages, 'tareImages', selectedScaleIds[0])
      }
    }
  }

  const callRequestImages = (images, path, scaleId) => {
    if ((images || []).length === 0 && !isNativeCamera) {
      requestImages(imageData => {
        setValue(path, imageData, { shouldDirty: true })
      }, scaleId)
    }
  }

  const onChangeGross = value => {
    const gross = parseInt(value || 0, 10)
    const tare = parseInt(getValues('tare') || 0, 10)

    setValue('gross', gross)
    setValue('net', gross - tare)

    callRequestImages(grossImages, 'grossImages')
  }

  const onChangeTare = value => {
    const tare = parseInt(value || 0, 10)
    const gross = parseInt(getValues('gross') || 0, 10)

    setValue('tare', tare)
    setValue('net', gross - tare)

    callRequestImages(tareImages, 'tareImages')
  }

  const onClickVoid = async () => {
    setShowVoidModal(false)

    await voidFinishedGood({ id: finishedGoodId })
      .unwrap()
      .then(() => {
        handleVoidSuccess()
        onModalClose()
      })
      .catch(error => handleVoidError(error))
  }

  const onEdit = () => {
    dispatch(
      showFormModal({
        formModalName: FORM_MODALS.NEW_FINISHED_GOOD,
        formModalProps: {
          viewMode: MODAL_VIEW_MODES.EDIT,
          navigateTo: '/app/inventory/finished-goods',
        },
      }),
    )
  }

  const dateNowFormatted =
    viewMode === MODAL_VIEW_MODES.VIEW ? 'Last Updated: ' + dateFormatter(finishedGoodItem?.status?.date) : ''

  const effectiveDate = dateFormatter(finishedGoodItem?.effectiveDate)

  useEffect(() => {
    if (finishedGoodItem && listOfFinishedGoods?.current) {
      const { tag, purchaseOrderNumber } = finishedGoodItem
      const infoIds = purchaseOrderNumber ? `${tag}-${purchaseOrderNumber}` : tag
      setTitle(`#${infoIds}`)
      reset({
        materialTypeId: finishedGoodItem?.materialType?.id || '',
        orderEntryId: finishedGoodItem.orderEntryId,
        materialName: finishedGoodItem?.materialType?.commonName || '',
        effectiveDate: dayjs(finishedGoodItem?.effectiveDate),
        gross: finishedGoodItem?.weight?.gross,
        tare: finishedGoodItem?.weight?.tare,
        net: finishedGoodItem?.weight?.net,
        type: listOfFinishedGoods?.current.find(item => finishedGoodItem?.type === item?.name)?.id || '',
        externalNotes: finishedGoodItem?.notes?.find(note => note.internal === false)?.name || undefined,
        internalNotes: finishedGoodItem?.notes?.find(note => note.internal === true)?.name || undefined,
        purchaseOrderNumber: finishedGoodItem?.purchaseOrderNumber,
        grossImages: finishedGoodItem?.grossImages,
        tareImages: finishedGoodItem?.tareImages,
        inboundTicketId: finishedGoodItem?.inboundTicketId,
        itemCount: finishedGoodItem?.itemCount,
      })
    }
  }, [finishedGoodItem, listOfFinishedGoods])

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  return (
    <>
      <AlertModal
        open={showVoidModal}
        modalType="void"
        title="Void This Finished Good"
        question="Are you sure you want to void this finished good?"
        description={`Finished good #${finishedGoodId} will be voided and all data will be lost.`}
        confirmLabel="YES, CONFIRM VOID"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowVoidModal(false)
        }}
        onSubmit={onClickVoid}
      />
      <ScalePopover
        scalePopoverData={scalePopoverData}
        setScalePopoverData={setScalePopoverData}
        callReadScale={callReadScale}
        onScaleSelected={scaleId => {
          const weightType = scalePopoverData.weightType
          if (weightType === 'gross') {
            callRequestImages(grossImages, 'grossImages', scaleId)
          } else if (weightType === 'tare') {
            callRequestImages(tareImages, 'tareImages', scaleId)
          }
        }}
      />
      <MultiImageBlade
        multiImageBladeData={multiImageBladeData}
        setMultiImageBladeData={setMultiImageBladeData}
        generateSaveImagesFunction={imageType => imageData => {
          setValue(`${imageType}Images`, imageData, { shouldDirty: true })
          setMultiImageBladeData(getValues())
        }}
        disabledUploadImages={viewMode === MODAL_VIEW_MODES.VIEW}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        {isLoading && <Loading />}
        <Stack height="100vh">
          <FormModalHeading
            title={title === 'Create New Finished Good' ? title : `Finished Good ${title}`}
            subtitle={dateNowFormatted}
            onBack={onModalClose}
            isInLockedAccountingPeriodV2={isInLockedAccountingPeriodV2}
            type="finished good"
          />

          <Stack p={5} flexGrow={1} overflow="scroll">
            <Box>
              <Typography variant="h6" color="primary">
                Material
              </Typography>
              <Box py={1}>
                <Divider />
              </Box>
              <Stack mt={1} spacing={2}>
                <Grid container>
                  <Grid item md={6} xs={12}>
                    <MaterialAutocomplete
                      label="Search"
                      placeholder="Search by Material Name or Code"
                      variant="outlined"
                      defaultValue={{
                        commonName: finishedGoodItem?.materialType?.commonName || finishedGoodProps?.materialName,
                        code: finishedGoodItem?.materialType?.code || finishedGoodProps?.materialCode,
                        materialTypeId: finishedGoodItem?.materialType?.id || finishedGoodProps?.materialTypeId,
                      }}
                      disabled={viewMode === MODAL_VIEW_MODES.VIEW || finishedGoodProps?.rowData}
                      onChange={material => {
                        setValue('materialTypeId', material?.id)
                        setValue('materialName', material?.commonName)
                      }}
                      TextFieldProps={{
                        error: !!errors.materialTypeId,
                        helperText: errors.materialTypeId?.message,
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item md={6} xs={12}>
                    <Select
                      name="type"
                      label="Type"
                      defaultValue={listOfFinishedGoods?.current?.[0]?.id || ''}
                      control={control}
                      items={listOfFinishedGoods?.current}
                      disabled={viewMode === MODAL_VIEW_MODES.VIEW}
                      error={!!errors.type}
                      selectProps={{
                        onClick: event => {
                          const finishedGoodTypeId = event.target.closest('li').dataset.value
                          setValue('type', finishedGoodTypeId)
                          if (finishedGoodProps?.rowData) {
                            // Do not load default tare when creating fg from ibt material row
                            return
                          }

                          const finishedGoodTypeData = listOfFinishedGoods?.current?.find(
                            finishedGoodType => finishedGoodType.id === finishedGoodTypeId,
                          )
                          const defaultTare = finishedGoodTypeData?.defaultTare
                          if (defaultTare || defaultTare === '0') {
                            onChangeTare(defaultTare)
                          }
                        },
                      }}
                    />
                    <FormHelperText error>{errors.type?.message}</FormHelperText>
                  </Grid>
                </Grid>
              </Stack>
            </Box>

            <Box mt={3}>
              <Typography variant="h6" color="primary">
                Finished Good Info
              </Typography>
              <Box py={1}>
                <Divider />
              </Box>
              <Stack mt={1}>
                <Grid container mb={2}>
                  <Grid item md={6} xs={12}>
                    <DateTimePicker
                      name="effectiveDate"
                      label="Effective Date"
                      control={control}
                      variant="outlined"
                      defaultDateTime={dayjs()}
                      dateTimePickerProps={{
                        disabled: viewMode === MODAL_VIEW_MODES.VIEW,
                        size: 'small',
                        'data-test': 'fg-effective-datetime-picker',
                        timezone: defaultYardTimezone,
                        minDate:
                          viewMode !== MODAL_VIEW_MODES.VIEW && lockAccountingPeriod.isLockedForAccounting
                            ? dayjs(lockAccountingPeriod.lockAccountingDate).add(1, 'day')
                            : undefined,
                      }}
                      error={!!errors?.effectiveDate}
                      helperText={errors?.effectiveDate?.message}
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={1} mb={2}>
                  <Grid
                    item
                    md={viewMode !== MODAL_VIEW_MODES.VIEW && showScaleButton ? 3.5 : 5}
                    xs={viewMode !== MODAL_VIEW_MODES.VIEW && showScaleButton ? 7 : 10}
                  >
                    <Input
                      name="gross"
                      type="number"
                      label="Gross Weight"
                      defaultValue=""
                      control={control}
                      disabled={viewMode === MODAL_VIEW_MODES.VIEW || isFetchingScaleReading}
                      pattern="[0-9]*"
                      error={!!errors.gross}
                      helperText={errors.gross?.message}
                      onChange={event => onChangeGross(event?.target?.value)}
                      inputRef={grossRef}
                    />
                  </Grid>
                  {viewMode !== MODAL_VIEW_MODES.VIEW && showScaleButton && (
                    <Grid item md={1.5} xs={3}>
                      <Button
                        name="read-scale"
                        variant="outlined"
                        size="small"
                        sx={{ width: '100%', height: '100%' }}
                        onClick={event => {
                          scaleButtonClickHandler('gross', event)
                        }}
                      >
                        {t('take_weight')}
                      </Button>
                    </Grid>
                  )}
                  <Grid item md={1} xs={2} alignContent="center">
                    <Stack direction="row" justifyContent="space-around">
                      <CameraButton
                        showBadge={!!grossImages?.length}
                        handleCameraClick={() => setMultiImageBladeData(values)}
                        disabled={selectedCameraIds?.length > 0 && !grossImages?.length}
                      />
                      {viewMode === MODAL_VIEW_MODES.EDIT && (
                        <Tooltip title="Add weight adjustment" enterDelay={300}>
                          <IconButton size="small" onClick={() => setShowGrossAdjustment(!showGrossAdjustment)}>
                            {showGrossAdjustment ? <ExposureIcon /> : <ExposureTwoToneIcon />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
                {viewMode === MODAL_VIEW_MODES.EDIT && (
                  <Collapse in={showGrossAdjustment} sx={{ overflow: 'visible' }}>
                    <Grid container spacing={1} mb={2}>
                      <Grid item md={3.5} xs={7}>
                        <Input
                          name="grossAdjustment"
                          type="number"
                          label="Gross Weight Adjustment"
                          defaultValue="0"
                          control={control}
                          pattern="[0-9]*"
                          onBlur={event => {
                            if (!event.target.value) {
                              setValue('grossAdjustment', '0')
                            }
                          }}
                        />
                      </Grid>
                      <Grid item md={1.5} xs={3}>
                        <Button
                          name="apply-gross-adjustment"
                          variant="outlined"
                          sx={{ width: '100%', height: '100%' }}
                          disabled={isFetchingScaleReading}
                          onClick={() => {
                            const currentGross = getValues('gross')
                            const grossAdjustment = getValues('grossAdjustment')
                            onChangeGross(Number(currentGross) + Number(grossAdjustment))
                          }}
                        >
                          {t('apply')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Collapse>
                )}
                <Grid container spacing={1} mb={2}>
                  <Grid
                    item
                    md={viewMode !== MODAL_VIEW_MODES.VIEW && showScaleButton ? 3.5 : 5}
                    xs={viewMode !== MODAL_VIEW_MODES.VIEW && showScaleButton ? 7 : 10}
                  >
                    <Input
                      name="tare"
                      type="number"
                      label="Tare Weight"
                      defaultValue=""
                      control={control}
                      disabled={viewMode === MODAL_VIEW_MODES.VIEW || isFetchingScaleReading}
                      pattern="[0-9]*"
                      error={!!errors.tare}
                      helperText={errors.tare?.message}
                      onChange={event => onChangeTare(event?.target?.value)}
                      inputRef={tareRef}
                    />
                  </Grid>
                  {viewMode !== MODAL_VIEW_MODES.VIEW && showScaleButton && (
                    <Grid item md={1.5} xs={3}>
                      <Button
                        name="read-scale"
                        variant="outlined"
                        size="small"
                        sx={{ width: '100%', height: '100%' }}
                        onClick={event => {
                          scaleButtonClickHandler('tare', event)
                        }}
                      >
                        {t('take_weight')}
                      </Button>
                    </Grid>
                  )}
                  <Grid item md={1} xs={2} alignContent="center">
                    <Stack direction="row" justifyContent="space-around">
                      <CameraButton
                        showBadge={!!tareImages?.length}
                        handleCameraClick={() => setMultiImageBladeData(values)}
                        disabled={selectedCameraIds?.length > 0 && !tareImages?.length}
                      />
                      {viewMode === MODAL_VIEW_MODES.EDIT && (
                        <Tooltip title="Add weight adjustment" enterDelay={300}>
                          <IconButton size="small" onClick={() => setShowTareAdjustment(!showTareAdjustment)}>
                            {showTareAdjustment ? <ExposureIcon /> : <ExposureTwoToneIcon />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
                {viewMode === MODAL_VIEW_MODES.EDIT && (
                  <Collapse in={showTareAdjustment} sx={{ overflow: 'visible' }}>
                    <Grid container spacing={1} mb={2}>
                      <Grid item md={3.5} xs={7}>
                        <Input
                          name="tareAdjustment"
                          type="number"
                          label="Tare Weight Adjustment"
                          defaultValue="0"
                          control={control}
                          pattern="[0-9]*"
                          onBlur={event => {
                            if (!event.target.value) {
                              setValue('tareAdjustment', '0')
                            }
                          }}
                        />
                      </Grid>
                      <Grid item md={1.5} xs={3}>
                        <Button
                          name="apply-tare-adjustment"
                          variant="outlined"
                          sx={{ width: '100%', height: '100%' }}
                          disabled={isFetchingScaleReading}
                          onClick={() => {
                            const currentTare = getValues('tare')
                            const tareAdjustment = getValues('tareAdjustment')
                            onChangeTare(Number(currentTare) + Number(tareAdjustment))
                          }}
                        >
                          {t('apply')}
                        </Button>
                      </Grid>
                    </Grid>
                  </Collapse>
                )}
                <Grid container mb={enablePullApartFeatures ? 2 : 0}>
                  <Grid item md={6} xs={12}>
                    <Input
                      name="net"
                      type="number"
                      label="Net Weight"
                      defaultValue=""
                      control={control}
                      disabled
                      error={!!errors.net}
                      helperText={errors.net?.message}
                    />
                  </Grid>
                </Grid>
                {enablePullApartFeatures && (
                  <Grid container spacing={viewMode === MODAL_VIEW_MODES.EDIT ? 1 : 0}>
                    <Grid
                      item
                      md={viewMode === MODAL_VIEW_MODES.EDIT ? 3.5 : 6}
                      xs={viewMode === MODAL_VIEW_MODES.EDIT ? 7 : 12}
                    >
                      <Input
                        name="itemCount"
                        label="Count"
                        defaultValue=""
                        control={control}
                        disabled={viewMode === MODAL_VIEW_MODES.VIEW}
                        error={!!errors.itemCount}
                        helperText={errors.itemCount?.message}
                        InputProps={{
                          inputComponent: InputFormat,
                          inputProps: {
                            inputType: 'number',
                            thousandSeparator: false,
                            allowNegative: false,
                            decimalScale: 0,
                          },
                        }}
                      />
                    </Grid>
                    {viewMode === MODAL_VIEW_MODES.EDIT && (
                      <>
                        <Grid item md={1.25} xs={2.5}>
                          <Button
                            name="item-count-plus-one"
                            variant="outlined"
                            sx={{ width: '100%', height: '100%' }}
                            onClick={() => {
                              setValue('itemCount', Number(getValues('itemCount')) + 1)
                            }}
                          >
                            +1
                          </Button>
                        </Grid>
                        <Grid item md={1.25} xs={2.5}>
                          <Button
                            name="item-count-minus-one"
                            variant="outlined"
                            sx={{ width: '100%', height: '100%' }}
                            onClick={() => {
                              setValue('itemCount', Number(getValues('itemCount')) - 1)
                            }}
                          >
                            -1
                          </Button>
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}
              </Stack>
            </Box>
            <Box mt={5} mb={3}>
              <Typography variant="h6" color="primary">
                Additional Information
              </Typography>
              <Box py={1}>
                <Divider />
              </Box>
              <Stack mt={1} spacing={2}>
                {linkedInboundTicketInfo?.customerId && linkedInboundTicketInfo?.customerName && (
                  <Grid container>
                    <Grid item ml={1}>
                      <InputLabel shrink>Customer</InputLabel>
                      <StyledLink
                        to={`/app/customers/${linkedInboundTicketInfo.customerId}`}
                        data-testid={`customer-id-${linkedInboundTicketInfo.customerId}`}
                        onClick={event => {
                          event.preventDefault()
                          window.open(`/app/customers/${linkedInboundTicketInfo.customerId}`, '_blank')
                        }}
                        style={{ fontWeight: 'normal' }}
                      >
                        {linkedInboundTicketInfo.customerName}
                      </StyledLink>
                    </Grid>
                  </Grid>
                )}
                {linkedInboundTicketInfo?.id && linkedInboundTicketInfo?.ticketId && (
                  <Grid container>
                    <Grid item ml={1}>
                      <InputLabel shrink>Inbound Ticket #</InputLabel>
                      <StyledLink
                        to={`/app/purchases/ticket-library/${linkedInboundTicketInfo.id}`}
                        data-testid={`customer-id-${linkedInboundTicketInfo.id}`}
                        onClick={event => {
                          event.preventDefault()
                          window.open(`/app/purchases/ticket-library/${linkedInboundTicketInfo.id}`, '_blank')
                        }}
                        style={{ fontWeight: 'normal' }}
                      >
                        {linkedInboundTicketInfo.ticketId}
                      </StyledLink>
                    </Grid>
                  </Grid>
                )}
                <Grid container>
                  <Grid item md={6} xs={12}>
                    <Input
                      name="purchaseOrderNumber"
                      label="PO#"
                      defaultValue=""
                      control={control}
                      disabled={viewMode === MODAL_VIEW_MODES.VIEW}
                    />
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={12}>
                    <Input
                      name="externalNotes"
                      label="External Notes"
                      defaultValue=""
                      control={control}
                      disabled={viewMode === MODAL_VIEW_MODES.VIEW}
                      inputProps={{
                        maxLength: 80,
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid item xs={12}>
                    <Input
                      name="internalNotes"
                      label="Internal Notes"
                      defaultValue=""
                      control={control}
                      disabled={viewMode === MODAL_VIEW_MODES.VIEW}
                      inputProps={{
                        maxLength: 80,
                      }}
                    />
                  </Grid>
                </Grid>
              </Stack>
            </Box>

            {viewMode === MODAL_VIEW_MODES.VIEW && (
              <Box mt={3}>
                <Button onClick={() => handleOpenPrintDialog(true)} variant="outlined">
                  Print Bale Tag
                </Button>
              </Box>
            )}

            {showPrintBaleTagModal && (
              <PrintDialog
                title={title}
                open={showPrintBaleTagModal}
                onClose={handleClosePrintDialog}
                dialogType={enablePullApartFeatures ? PRINT_DIALOG_TYPES.PULL_APART_TAG : PRINT_DIALOG_TYPES.TAG}
                showLogoAddress
              >
                <BaleTag
                  createdDate={'Effective Date: ' + effectiveDate}
                  materialName={values?.materialName}
                  gross={parseInt(values?.gross, 10)}
                  tare={parseInt(values?.tare, 10)}
                  net={parseInt(values?.net, 10)}
                  qrCodeValue={finishedGoodItem?.tag}
                  notes={finishedGoodItem?.notes.find(note => note.internal === false).name || ''}
                />
              </PrintDialog>
            )}
          </Stack>

          <FormModalActionBar
            submitActionText={viewMode === MODAL_VIEW_MODES.EDIT ? 'EDIT' : 'Create Finished Good'}
            deleteActionText={viewMode === MODAL_VIEW_MODES.NEW ? '' : 'Delete'}
            onCancel={onModalClose}
            viewMode={viewMode}
            onEdit={onEdit}
            onDelete={() => {
              setShowVoidModal(true)
            }}
            isInLockedAccountingPeriodV2={isInLockedAccountingPeriodV1}
            isFormEditable={!(viewMode === MODAL_VIEW_MODES.VIEW && finishedGoodProps?.rowData)}
            isFormVoidable={!(viewMode === MODAL_VIEW_MODES.VIEW && finishedGoodProps?.rowData)}
          />
        </Stack>
      </form>
    </>
  )
}

CreateFinishedGoodContainer.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

CreateFinishedGoodContainer.defaultProps = {
  onFormStateChange: () => {},
  onHideModal: null,
}

export default CreateFinishedGoodContainer
